import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { get } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { PageHeader, Tabs } from '@/components/ui/layout';
import { Alert, PageSkeleton } from '@/components/ui/primitives';
import { BillingSettingsTab } from '@/features/admin/BillingSettingsTab';
import { BranchTaxRatesTab } from './BranchTaxRatesTab';

const TABS = ['cash', 'tax'];

/** Cash and payment rules, kept with the money screens rather than under Administration. */
export function BillingSettingsPage() {
  const { can, canWrite } = useAuth();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(() => {
    const wanted = params.get('tab');
    return wanted && TABS.includes(wanted) ? wanted : 'cash';
  });
  const settings = useQuery({
    queryKey: ['tenant-settings'],
    queryFn: () => get<{ settings: Record<string, unknown> }>('/org/settings'),
  });
  const readOnly = !can('tenant.settings.manage') || !canWrite;

  return (
    <>
      <PageHeader title={tr('billing', 'billing_settings')} description={tr('billing', 'cash_shifts_and_payment_rules')} />
      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'cash', label: tr('billing', 'cash_and_payments') },
          { id: 'tax', label: tr('billing', 'tax_rates') },
        ]}
      />
      {readOnly && !settings.isLoading && <Alert className="mb-4">{tr('billing', 'you_can_view_these_settings_but_not_change_them')}</Alert>}
      {tab === 'cash' && settings.isLoading && <PageSkeleton />}
      {tab === 'cash' && settings.data && <BillingSettingsTab readOnly={readOnly} settings={settings.data.settings ?? {}} />}
      {tab === 'tax' && <BranchTaxRatesTab readOnly={readOnly} />}
    </>
  );
}
